import React, { useMemo, useState } from 'react';
import { StyleSheet, TouchableOpacity } from 'react-native';

import { Stack } from '@/components/layout';
import { SvgIcon, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { PlayerProfileNoteDetailsSheet } from '@/features/player/components/player-profile/player-note/player-profile-note-details-sheet';
import { PlayerProfileNoteEditorSheet } from '@/features/player/components/player-profile/player-note/player-profile-note-editor-sheet';
import type { PlayerProfileNoteData } from '@/features/player/components/types';
import { createStyles, useScaling } from '@/hooks';
import { colors } from '@/theme/colors';
import { fontFamilies } from '@/theme/typography';
import { format } from 'date-fns';

type PlayerProfileNotesContentProps = {
  notes?: PlayerProfileNoteData[];
  onAddNewNote?: () => void;
  onPressNoteDetail?: (note: PlayerProfileNoteData) => void;
  onSaveNote?: (payload: { title: string; detail: string }) => Promise<void>;
};

function formatCreatedDate(date: Date): string {
  return format(date, 'EEEE, MMMM d, yyyy').toUpperCase();
}

export function PlayerProfileNotesContent({
  notes = [],
  onAddNewNote,
  onPressNoteDetail,
  onSaveNote,
}: PlayerProfileNotesContentProps) {
  const styles = useStyles();
  const { scale, isTablet } = useScaling();
  const [selectedNote, setSelectedNote] = useState<PlayerProfileNoteData | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const sortedNotes = useMemo(
    () => [...notes].sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime()),
    [notes],
  );
  return (
    <Stack
      flexGrow={1}
      backgroundColor={!isTablet ? 'white' : 'transparent'}
      paddingX={scale({ phone: 18, tablet: 24 })}
      paddingTop={scale(16)}>
      <AddNewNoteButton
        onPress={() => {
          if (onAddNewNote) {
            onAddNewNote();
            return;
          }
          setEditorOpen(true);
        }}
      />

      {sortedNotes.length === 0 ? null : (
        <Stack style={styles.list}>
          {sortedNotes.map(note => (
            <NoteCard
              key={note.id}
              note={note}
              onPressDetail={() => {
                if (onPressNoteDetail) {
                  onPressNoteDetail(note);
                } else {
                  setSelectedNote(note);
                  setDetailsOpen(true);
                }
              }}
            />
          ))}
        </Stack>
      )}
      <PlayerProfileNoteDetailsSheet
        open={detailsOpen}
        note={selectedNote}
        onOpenChange={open => {
          setDetailsOpen(open);
          if (!open) setSelectedNote(null);
        }}
      />
      <PlayerProfileNoteEditorSheet
        open={editorOpen}
        onOpenChange={setEditorOpen}
        onSave={onSaveNote}
      />
    </Stack>
  );
}

function AddNewNoteButton({ onPress }: { onPress?: () => void }) {
  const styles = useStyles();
  const { isTablet, scale } = useScaling();

  return (
    <TouchableOpacity style={styles.addButton} onPress={onPress}>
      <Stack direction="row" align="center" justify="center" gap={10}>
        <SvgIcon name="IcPlus" size={scale(24)} color={colors['0']} />
        <Text
          variant={
            isTablet ? 'Font Inter/Subtitle/Medium 12px' : 'Font Inter/Subtitle/Regular 11px'
          }
          style={styles.addButtonText}>
          {STRINGS.player.profile.notesContent.addNewNote}
        </Text>
      </Stack>
    </TouchableOpacity>
  );
}

function NoteCard({
  note,
  onPressDetail,
}: {
  note: PlayerProfileNoteData;
  onPressDetail?: () => void;
}) {
  const styles = useStyles();
  const { isTablet, scale } = useScaling();

  return (
    <Stack style={styles.card}>
      <Stack direction="row" style={styles.dateRow}>
        <Stack style={styles.noteAccent} />
        <Text
          variant={isTablet ? 'Font Inter/Subtitle/Medium 12px' : 'Font Inter/Subtitle/Medium 11px'}
          style={styles.dateText}>
          {formatCreatedDate(note.createdAt)}
        </Text>
      </Stack>
      <Stack style={styles.divider} />

      <Stack direction="row" style={styles.noteBody}>
        <Stack style={styles.noteAccentContent} />
        <Stack flex={1} style={styles.noteContent}>
          <Text
            variant={isTablet ? undefined : 'Font Montserrat/Subtitle/Minimize 2 Bold 10px'}
            fontWeight={'800'}
            fontFamily={isTablet ? 'Montserrat' : undefined}
            fontSize={isTablet ? scale(12) : undefined}
            style={styles.noteTitle}>
            {note.title}
          </Text>
          <Text
            numberOfLines={5}
            ellipsizeMode="tail"
            variant={isTablet ? 'Font Inter/Subtitle/Regular 12px' : 'Font Inter/Subtitle/Regular'}
            style={styles.noteDetail}>
            {note.detail}
          </Text>
          <Stack flex={1} />
          <TouchableOpacity style={styles.detailsButton} onPress={onPressDetail}>
            <Text
              variant={isTablet ? undefined : 'Font Montserrat/Subtitle/Minimize 2 Medium 10px'}
              fontFamily={isTablet ? 'Montserrat' : undefined}
              fontSize={isTablet ? scale(11) : undefined}
              fontWeight={isTablet ? '400' : undefined}
              style={styles.detailsText}>
              {STRINGS.player.profile.notesContent.details}
            </Text>
            <SvgIcon name="IcBack" size={scale(18)} color="#EA7D24" style={styles.detailsIcon} />
          </TouchableOpacity>
        </Stack>
      </Stack>
    </Stack>
  );
}

const useStyles = createStyles(({ scale, isTablet }) => ({
  root: {
    flex: 1,
  },
  contentContainer: {
    flexGrow: 1,
    paddingHorizontal: scale({ phone: 18, tablet: 24 }),
    paddingTop: scale(16),
  },
  addButton: {
    backgroundColor: colors.Blue,
    borderRadius: scale(4),
    height: scale(40),
    paddingVertical: 0,
    paddingHorizontal: 0,
    width: isTablet ? scale(240) : '100%',
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOpacity: 0.2,
    shadowRadius: 4,
    shadowOffset: { width: 0, height: 2 },
    elevation: 3,
  },
  addButtonText: {
    color: colors['0'],
    textTransform: 'uppercase',
  },
  list: {
    marginTop: scale(12),
    gap: scale(12),
  },
  card: {
    backgroundColor: colors['0'],
    borderRadius: scale(2),
    shadowColor: '#000',
    shadowOpacity: 0.12,
    shadowRadius: 4,
    shadowOffset: { width: 0, height: 2 },
    elevation: 2,
  },
  dateRow: {},
  dateText: {
    color: colors.Blue,
    paddingHorizontal: scale(9),
    paddingVertical: scale(13),
  },
  divider: {
    height: StyleSheet.hairlineWidth,
    backgroundColor: colors['4'],
  },
  noteBody: {
    minHeight: scale(96),
  },
  noteAccent: {
    width: scale(9),
    backgroundColor: colors['Level 1'],
    borderTopLeftRadius: scale(2),
  },
  noteAccentContent: {
    width: scale(9),
    backgroundColor: colors['Blue 2'],
    borderBottomLeftRadius: scale(2),
  },
  noteContent: {
    paddingHorizontal: scale(9),
    paddingTop: scale(8),
    paddingBottom: scale(4),
    gap: scale(4),
  },
  noteTitle: {
    color: colors.Blue,
    textTransform: 'uppercase',
  },
  noteTitleTablet: {
    fontSize: scale(12),
    fontWeight: '800',
    fontFamily: fontFamilies.montserrat,
  },
  noteDetail: {
    color: colors['6'],
  },
  detailsButton: {
    height: scale(24),
    marginLeft: 'auto',
    marginRight: scale(4),
    flexDirection: 'row',
    alignItems: 'center',
    gap: scale(4),
  },
  detailsText: {
    color: colors.Blue,
    textTransform: 'uppercase',
  },
  detailsTextTablet: {
    fontSize: scale(11),
    fontWeight: '400',
    fontFamily: fontFamilies.montserrat,
  },
  detailsIcon: {
    transform: [{ rotate: '180deg' }],
  },
}));
